// SLOT 7.4 - the data behind the strip, kept out of the markup.
//
// Everything in here exists because of one honest problem: the feed can be
// empty, thin or months out of date, and the section has to look deliberate
// in all three cases.
//
// WHERE THE TILES COME FROM. GET /api/v2/orders/recent_completed, served by
// Order::LiveFeed (app/services/order/live_feed.rb) and cached for five
// minutes. It returns completed orders whose photos passed the PII and
// quality scrub (feed_photos_status = 'safe'), newest first, with nine curated
// showcase photographs spliced in from position 5 so the strip is never
// empty on a quiet day. Each tile carries the base service, the outward
// postcode and the completion time, and nothing that identifies a customer.
//
// WHY THE TIMESTAMPS ARE GATED. Verified 4 Oct 2026: the newest publishable
// wash anywhere was 28 May, because a completion bug meant no wash was queued
// for photo processing for four months. That is fixed and a backlog is
// processing, but the fix does not change what this component has to survive:
// a strip of real washes that are four months old must not be dressed up as
// "just now".
//
// So liveness is earned twice, and this is the one improvement on the old
// component's logic:
//
//   1. The SECTION qualifies only when at least four real washes landed
//      inside 48 hours (MIN_RECENT_FOR_LIVE / RECENT_WINDOW_MS). One fresh
//      wash among stale ones is not a live feed.
//   2. A TILE shows a time only if that tile is itself inside the window.
//      The old component showed a relative time on every tile once the
//      section qualified, which could print "28 May" next to "2h ago" and
//      read as a business that washed four cars this week and nothing since.
//
// Below the gate there is no LIVE treatment to fall back from: the heading and
// the intro say the same true thing either way (see ./index.js), so a slow
// spell costs the section its timestamps and nothing else. There is no LIVE
// badge, both because DESIGN.md §3 forbids badges and because a pulsing dot
// was making a claim this data cannot always support.
//
// WHAT CHANGED ON 4 OCT, besides the copy:
//
//   The curated frames no longer come from ../hero/frames.js. That file is
//   the fold's list and the fold cut itself to five frames; this section was
//   reading nine names out of it and silently losing four. ./showcase.js holds
//   all nine and says what should replace it.
//
//   A tile labelled with a service we have retired is held back rather than
//   relabelled (./showcase.js, RETIRED_SERVICES).
//
//   The caption gained the London district in front of the postcode, from the
//   same list the area pages are built from. "Putney SW15" is a place; a
//   postcode on its own is a code, and this section is read by people who
//   live two streets away from the car in the photograph.

import { useEffect, useState } from 'react'

import { AREAS, RETIRED_AREAS } from '../areas-cta/areas'
import { image } from '../../../../lib/image'
import { settings } from '../../../../store/constants'
import { CURATED_ORDER, RETIRED_SERVICES, SHOWCASE_TILES } from './showcase'

// Always same-origin, so each environment shows the photos in its own DB.
// `settings.host` is empty in deployed environments, so this is a relative
// URL that hits whatever origin served the page.
const FEED_URL = `${settings.host}/api/v2/orders/recent_completed`

export const RECENT_WINDOW_MS = 48 * 60 * 60 * 1000
export const MIN_RECENT_FOR_LIVE = 4

// Thirty frames is the strip's ceiling. The endpoint will serve up to sixty;
// every frame past the first few is a lazy request a visitor pays for only by
// swiping, but sixty portrait photographs is a scroll nobody finishes, and
// the section has to look deliberate at thirty rather than merely long.
export const MAX_FRAMES = 30

// The frames are 3:4 boxes and the photographs are cropped to fill them
// (object-fit: cover in ./recent-work.css). The curated shots are
// natively 640 x 853, so they are uncropped; a landscape photograph from an
// operator's phone is centre-cropped to match. These two numbers are the
// width and height attributes every <img> carries so the browser reserves
// the box before the bytes land (DESIGN.md §3, CLS 0.05 or less).
export const FRAME_W = 480
export const FRAME_H = 640

// How many skeleton frames stand in while the fetch is in flight. Four is
// what a 1440 row shows, so the strip is exactly as tall before the data
// lands as after it: the section never grows and never shifts the page.
export const SKELETON_FRAMES = 4

// ---------------------------------------------------------------------------
// Where a wash happened, in words
// ---------------------------------------------------------------------------
// The endpoint sends an outward postcode and nothing else: "SW15". That is as
// much as it should send, because it is the most it can send without
// narrowing a photograph down to a street. But a postcode is a code, and the
// people reading this section live inside the ones in it.
//
// AREAS is the 46 area pages, each with the outward codes it covers. It is
// itself a generated mirror of seo/areas.yml, which is the file the area pages
// and their wash counts are built from, so "SW15 is Putney" is not a judgement
// made here: it is the same mapping that decides which page a Putney search
// lands on. Importing it is a data import and drags no stylesheet with it.
//
// The six places whose pages were retired on 2026-10-15 keep their names
// here (RETIRED_AREAS): a Canary Wharf photo is still a Canary Wharf photo.
//
// A code we do not cover falls back to the code alone, which is what the
// caption said before. E2 and TW10 are both in the curated set and neither is
// in an area page, so this degrades on real data on every render, which is the
// best possible way for a fallback to be exercised.
const AREA_BY_OUTCODE = [...AREAS, ...RETIRED_AREAS].reduce((map, area) => {
  area.outcodes.forEach(outcode => {
    map[outcode] = area.name
  })
  return map
}, {})

// "Putney SW15", or "SW15", or "London" for a wash whose address had no
// readable postcode in it. No comma: the name and the code are one address,
// not two facts, and the caption already uses middle dots to separate facts.
export const placeLabel = area => {
  if (!area) return 'London'
  const name = AREA_BY_OUTCODE[area]
  return name ? `${name} ${area}` : area
}

// ---------------------------------------------------------------------------
// The curated frames
// ---------------------------------------------------------------------------
const CURATED_BY_FILE = SHOWCASE_TILES.reduce((map, tile) => {
  map[tile.file] = tile
  return map
}, {})

// THE FILE NAME COMES OFF THE WIRE, NOT OUT OF THE URL (3 Nov 2026).
//
// Every asset URL now carries a content digest, so the photo path on a
// showcase tile is "/assets/gallery-image-01-7c1f9a2e.webp" and its base name
// is no longer the key this file is built on. Order::LiveFeed#showcase_tiles
// therefore sends `file` beside the photo. A real wash's photo is a GCS URL
// and has no `file`, which is correct: it is not curated and the base-name
// fall back finds nothing for it either.
export const fileOf = item => {
  if (item && item.file) return item.file
  const src = item && typeof item === 'object' ? item.src : item
  return String(src || '').split('/').pop()
}

const curatedFor = item => CURATED_BY_FILE[fileOf(item)] || null

// Where a curated frame sits when the feed is not live. Anything the list does
// not name sorts to the end rather than vanishing, so a new showcase
// photograph added to the Ruby appears without an edit here.
const curatedRank = item => {
  const index = CURATED_ORDER.indexOf(fileOf(item))
  return index === -1 ? CURATED_ORDER.length : index
}

// ---------------------------------------------------------------------------
// The caption
// ---------------------------------------------------------------------------
// Two lines. The wash on the first, in ink: it is the one word on the tile a
// visitor is pricing. Everything else on the second, in grey, separated by
// middle dots: where it happened, how to read the photograph, and how long ago
// if the strip has earned a time.
//
// Both halves come from the order, so neither can be invented here. A tile
// that has lost its service label leads on the place instead, and a made-up
// service name on a real photograph is the one thing that would make this
// section worth less than stock photography.

// "Before and after", on the three frames that are one. A split frame at
// 256px is genuinely ambiguous: half the viewers read gallery-image-06 as two
// different cars, which turns the strongest proof in the strip into a
// confusing picture of some water.
//
// It is READ OUT OF THE ALT TEXT rather than listed separately, which is the
// whole point: the alt is a sentence somebody wrote while looking at the
// photograph, and a note derived from it cannot contradict it. The three that
// say so are 04, 06 and 09. A real wash's alt is generated by `describe`
// below and can never say it, which is correct: nothing tells us whether an
// operator's photograph is a before and after.
const BEFORE_AND_AFTER = /before and after/i
const noteFor = frame => (BEFORE_AND_AFTER.test(frame.alt || '') ? 'Before and after' : null)

// The lead line and the grey clauses under it, for one frame at one moment.
export const captionLines = (frame, time) => {
  const place = placeLabel(frame.area)
  const lead = frame.service || place
  const clauses = [frame.service ? place : null, frame.note, time].filter(Boolean)
  return { lead, clauses }
}

// Meaningful alt for a photograph we have not seen (DESIGN.md §7: gallery
// images carry meaningful alt). Degrades a clause at a time and never claims
// more than the order knows.
const describe = frame => {
  const service = frame.service ? `a ${frame.service}` : 'a wash'
  const where = frame.area ? ` in ${frame.area}` : ' in London'
  return `Photograph from ${service}${where}, taken by the valeter`
}

// Relative time, and deliberately only inside the 48 hour window: beyond it
// this returns null and the caption carries no date at all. A date is the
// thing that ages badly, so the component that cannot promise a fresh one
// prints none.
export const relativeTime = (iso, now = Date.now()) => {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null
  const elapsed = now - then
  if (elapsed < 0 || elapsed >= RECENT_WINDOW_MS) return null
  const minutes = Math.floor(elapsed / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return 'yesterday'
}

// One frame per wash, and only the first of its photographs. The endpoint
// serves up to three per order and the old component widened a tile to hold
// them, which gave the strip three different tile widths and no rhythm. One
// photograph per wash means every frame is the same size at every count,
// which is what makes three tiles and thirty tiles both look intended.
//
// Returns null for a tile that cannot be shown honestly: no photograph, or a
// service that is no longer in the catalogue. The second case drops a real
// wash as readily as a curated one, and it should: the label under a
// photograph is the evidence, and a label for something a visitor cannot buy
// is worse than one frame fewer.
const frameFor = item => {
  const src = (item.photos || []).find(Boolean)
  if (!src) return null

  // `item.file` for a curated frame, the photo's base name for anything else.
  const named = { file: item.file, src }
  const curated = curatedFor(named)
  const service = item.service || curated?.service || null
  if (service && RETIRED_SERVICES.includes(service)) return null

  const frame = {
    key: String(item.id),
    src,
    service,
    area: item.area || curated?.area || null,
    completedAt: item.completed_at || null,
    // A curated showcase shot, or a real wash with no completion time: either
    // way it is timeless and leads the strip when the feed is not live.
    curated: Boolean(curated) || !item.completed_at,
    rank: curatedRank(named)
  }
  frame.alt = curated?.alt || describe(frame)
  frame.note = noteFor(frame)
  return frame
}

// The evergreen gallery, for the case where the endpoint answers with nothing
// at all (it is down, or every photo is held back by the scrubber). The same
// curated frames in the same order as a normal quiet day, so the two quiet
// cases are indistinguishable to a visitor and nothing about the section
// announces that a request failed.
export const fallbackFrames = () =>
  CURATED_ORDER.map(file => CURATED_BY_FILE[file])
    .filter(tile => tile && !RETIRED_SERVICES.includes(tile.service))
    .map(tile => ({
      key: tile.file,
      src: image(`./${tile.file}`),
      service: tile.service,
      area: tile.area,
      alt: tile.alt,
      note: noteFor(tile),
      completedAt: null,
      curated: true,
      rank: curatedRank({ file: tile.file })
    }))

// Curated first when the feed is not live, and in ./showcase.js's order inside
// that: they are the best looking and they cannot age, so a stale strip opens
// on its strongest frames and on the before-and-afters the fold gave up. When
// the feed IS live the server's order is kept whole, because then the freshest
// real washes are the strongest thing in it and the curated frames are already
// spliced where Order::LiveFeed::SHOWCASE_POSITIONS wants them.
//
// Array#sort is stable, so real tiles keep the order the server sent them in.
const order = (frames, isLive) =>
  isLive
    ? frames
    : [...frames].sort((a, b) => Number(b.curated) - Number(a.curated) || a.rank - b.rank)

export const framesFrom = (items, now = Date.now()) => {
  const frames = (items || []).map(frameFor).filter(Boolean)
  if (frames.length === 0) return { frames: fallbackFrames(), isLive: false }

  const recent = frames.filter(
    frame => !frame.curated && frame.completedAt && relativeTime(frame.completedAt, now) !== null
  ).length
  const isLive = recent >= MIN_RECENT_FOR_LIVE

  return { frames: order(frames, isLive).slice(0, MAX_FRAMES), isLive }
}

// Fetch when the section comes within reach of the viewport, not on mount.
// The slot's brief is blunt about why: a fetch on the home route costs an LCP
// this rebuild cannot spend, and this section is a long way below the fold.
// The observer's margin is generous enough that the data is normally in hand
// before the strip is on screen, so in practice the visitor sees the frames
// and not the skeleton. Where IntersectionObserver is missing, the fetch
// happens on mount and the only cost is a request nobody needed.
export const useNearViewport = ref => {
  const [near, setNear] = useState(false)

  useEffect(() => {
    if (near) return undefined
    const element = ref.current
    if (!element || typeof IntersectionObserver === 'undefined') {
      setNear(true)
      return undefined
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setNear(true)
          observer.disconnect()
        }
      },
      { rootMargin: '600px 0px' }
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [ref, near])

  return near
}

// `pending` is true until the request has settled one way or the other. A
// failed request and an empty one are the same answer here: no items, which
// framesFrom turns into the evergreen gallery.
export const useRecentWashes = enabled => {
  const [state, setState] = useState({ pending: true, items: null })

  useEffect(() => {
    if (!enabled) return undefined
    let cancelled = false

    const settle = items => {
      if (!cancelled) setState({ pending: false, items })
    }

    fetch(FEED_URL, { headers: { Accept: 'application/json' } })
      .then(response => (response.ok ? response.json() : null))
      .then(json => settle(json?.status === 'error' || !Array.isArray(json?.data) ? [] : json.data))
      .catch(() => settle([]))

    return () => {
      cancelled = true
    }
  }, [enabled])

  return state
}
